import { apiService } from "./apiService";

// Typed client for the blog admin API (/api/admin/blog). Mirrors the pattern of
// newsletterAdminService. See .claude/tasks/blog-automation.md.

const BASE = "/api/admin/blog";

export type BlogStatus = "published" | "draft" | "archived" | "failed" | "generating";
export type BlogCategory = "Article" | "Resource" | "Update";

export interface BlogArticleListItem {
  id: string;
  slug: string;
  title: string;
  category: string;
  status: BlogStatus;
  pub_date: string;
  updated_date: string | null;
  hero_image_url: string | null;
  generation_error: string | null;
}

export interface BlogArticleFull extends BlogArticleListItem {
  meta_description: string | null;
  keywords: string | null;
  excerpt: string | null;
  body_markdown: string;
  author: string;
  generation_model: string | null;
}

export interface BlogTopic {
  id: string;
  position: number;
  title: string;
  description: string | null;
  target_keywords: string | null;
  category: string;
  status: string;
  attempts: number;
  last_error: string | null;
}

export interface BlogConfig {
  id: string;
  status: string;
  cadence: string;
  custom_cadence_days: number | null;
  publish_day_of_week: number | null;
  publish_hour_utc: number;
  llm_system_prompt: string;
  runtime_system_prompt: string | null;
  topic_batch_size: number;
  topic_refill_threshold: number;
  max_topic_attempts: number;
  generation_model: string | null;
  default_author: string;
  next_run_at: string | null;
  last_published_at: string | null;
}

export const blogAdminService = {
  // Articles
  async listArticles(status?: string): Promise<{ articles: BlogArticleListItem[]; total: number }> {
    const res = await apiService.get(`${BASE}/articles`, {
      params: status ? { status } : {},
    });
    return res.data;
  },
  async getArticle(id: string): Promise<BlogArticleFull> {
    const res = await apiService.get(`${BASE}/articles/${id}`);
    return res.data.article;
  },
  async updateArticle(id: string, patch: Partial<BlogArticleFull>): Promise<BlogArticleFull> {
    const res = await apiService.patch(`${BASE}/articles/${id}`, patch);
    return res.data.article;
  },
  async deleteArticle(id: string): Promise<void> {
    await apiService.delete(`${BASE}/articles/${id}`);
  },
  async uploadImage(id: string, file: File): Promise<string> {
    const form = new FormData();
    form.append("file", file);
    const res = await apiService.post(`${BASE}/articles/${id}/image`, form);
    return res.data.url;
  },
  async preview(markdown: string): Promise<string> {
    const res = await apiService.post(`${BASE}/preview`, { markdown });
    return res.data.html;
  },

  // Topics
  async listTopics(): Promise<BlogTopic[]> {
    const res = await apiService.get(`${BASE}/topics`);
    return res.data.topics;
  },
  async createTopic(input: Partial<BlogTopic>): Promise<BlogTopic> {
    const res = await apiService.post(`${BASE}/topics`, input);
    return res.data.topic;
  },
  async updateTopic(tid: string, patch: Partial<BlogTopic>): Promise<BlogTopic> {
    const res = await apiService.patch(`${BASE}/topics/${tid}`, patch);
    return res.data.topic;
  },
  async deleteTopic(tid: string): Promise<void> {
    await apiService.delete(`${BASE}/topics/${tid}`);
  },
  async planTopics(count?: number): Promise<number> {
    const res = await apiService.post(`${BASE}/topics/plan`, count ? { count } : {});
    return res.data.added;
  },

  // Config + publish
  async getConfig(): Promise<BlogConfig | null> {
    const res = await apiService.get(`${BASE}/config`);
    return res.data.config;
  },
  async updateConfig(patch: Partial<BlogConfig>): Promise<BlogConfig> {
    const res = await apiService.patch(`${BASE}/config`, patch);
    return res.data.config;
  },
  async publishNow(): Promise<{ published: number; skippedReason?: string; slug?: string }> {
    const res = await apiService.post(`${BASE}/publish-now`, {});
    return res.data;
  },
};
